import {
    isNil, map, isArray, isNull,
    transform, difference, forEach, isEmpty
} from 'lodash';

import { getTranslatedKPINameV2 } from 'helpers/translateHelper';
import { isWholeNumber, roundNumber } from 'helpers/numberHelper';
import { signalDataTypes } from './constants';
import { baseToCustomUOM, getMeasurementUnits } from 'helpers/unitHelper';
import { chartColors } from 'helpers/constants';

const convertAndFormatWithGroups = (timeseriesUnitGroupID, measurementValue, userMeasurementUnitGroups, timeseriesPrecision) => {
    const convertedValue = baseToCustomUOM?.convertAndFormatWithGroups(
        timeseriesUnitGroupID,
        measurementValue,
        userMeasurementUnitGroups);
    const formattedValue = isWholeNumber(convertedValue?.measurementValue) ? convertedValue?.measurementValue :
        roundNumber(convertedValue?.measurementValue, timeseriesPrecision)
            .toFixed(timeseriesPrecision);
    return formattedValue;
};

export const setColor = (list) => {
    const usedColors = transform(list, (acc, x) => {
        if (x.color) {
            acc.push(x.color);
        }
    }, []);

    const availableColors = difference(chartColors, usedColors);
    forEach(list, (item) => {
        if (!item.color) {
            item.color = availableColors.length ? availableColors.splice(0, 1)[0] : null;
        }
    });
    return list;
};

// eslint-disable-next-line import/no-unused-modules
export const createParamMeasurementComponentObjV2 = (measurement, componentObj) => {
    const measurementArray = isArray(measurement) ? measurement : [measurement];
    // Drop unresolved (falsy) KPI entries to avoid malformed API payloads
    return map(measurementArray.filter(Boolean), (kpi) => {
        return {
            assetId: componentObj?.id,
            timeseries: {
                timeseriesKey: kpi?.timeseriesKey
            }
        };
    });
};

// Helper function to calculate offset
const calculateOffset = (measurementType, userMeasurementUnitGroups) => {
    return convertAndFormatWithGroups(
        measurementType?.timeseriesUnitGroupID,
        measurementType.offset,
        userMeasurementUnitGroups,
        measurementType?.timeseriesPrecision
    );
};

// Helper function to apply calibration
const applyCalibration = (measurementType, measurementTypeValue) => {
    return measurementType.isCalibrationEnabled && measurementType.measurementCalibration
        ? measurementTypeValue + measurementType.measurementCalibration
        : measurementTypeValue;
};

// Helper function to format measurement value
const formatMeasurementValue = (measurementType, measurementTypeValue) => {
    return isWholeNumber(measurementTypeValue)
        ? measurementTypeValue
        : roundNumber(measurementTypeValue, measurementType.timeseriesPrecision).toFixed(measurementType.timeseriesPrecision);
};


// Helper function to check if the data type is non-signal
const isNonSignalDataType = (measurementType) => {
    return measurementType.dataType && !signalDataTypes.includes(measurementType?.dataType?.toLowerCase());
};

// Helper function to handle non-signal data types
const handleNonSignalDataType = (measurementType, userMeasurementUnitGroups) => {
    if (isNil(measurementType?.timeseriesLastValue)) { return '-'; }

    const offset = calculateOffset(measurementType, userMeasurementUnitGroups);
    let measurementTypeValue = measurementType.offset
        ? +measurementType.timeseriesLastValue + +offset
        : measurementType.timeseriesLastValue;

    measurementTypeValue = parseFloat(measurementTypeValue);

    if (!isNaN(measurementTypeValue)) {
        measurementTypeValue = applyCalibration(measurementType, measurementTypeValue);
        measurementTypeValue = formatMeasurementValue(measurementType, measurementTypeValue);
    }

    return measurementTypeValue;
};

// Helper function to check if the value is boolean
const isBooleanValue = (measurementType) => {
    return !isNil(measurementType?.timeseriesLastValue) && typeof measurementType?.timeseriesLastValue === 'boolean';
};

// Helper function to handle signal data types
const handleSignalDataType = (measurementType) => {
    if (isBooleanValue(measurementType)) {
        return measurementType?.timeseriesLastValue ? 1 : 0;
    }

    if (isNull(measurementType?.timeseriesLastValue)) {
        return '-';
    }

    return `${measurementType?.timeseriesLastValue}`;
};

const getMeasurementTypeValueV2 = (measurementType) => {
    if (!measurementType) { return '-'; }

    const userMeasurementUnitGroups = getMeasurementUnits();

    if (isNonSignalDataType(measurementType)) {
        return handleNonSignalDataType(measurementType, userMeasurementUnitGroups);
    }

    return handleSignalDataType(measurementType);
};


// eslint-disable-next-line import/no-unused-modules
export const mapTrendKpiListV2 = (trendKPIs) => {
    const sections = transform(trendKPIs, (acc, kpiSection) => {
        const data = {};

        kpiSection?.timeseries?.forEach((mType) => {
            //if (mType.isVisible) {
            const lastMeasurementValue = getMeasurementTypeValueV2(mType);
            data[mType.timeseriesKey] = {
                ...mType,
                label: getTranslatedKPINameV2(mType),
                value: mType?.timeseriesLastValue,
                displayValue: lastMeasurementValue,
            };
            // }
        });

        acc.push({
            sectionId: kpiSection.timeseriesGroupCode ? kpiSection.timeseriesGroupCode : kpiSection.timeseriesGroupName,
            data
        });
    }, []);

    return sections;
};
export const getFlatAvailableKPIList = (kpis) => {
    return kpis?.reduce((allKpiData, kpi) => {
        if (kpi?.data) {
            return { ...allKpiData, ...kpi.data };
        }
        return allKpiData;
    }, {}) || {};
};
const getKPIConfigForChartV2 = (kpi) => {
    return !isEmpty(kpi) && {
        appendCodeToTranslation: kpi.appendCodeToTranslation,
        displayName: getTranslatedKPINameV2(kpi),
        measurementTypeCode: kpi.timeseriesGroupCode,
        measurementTypeIdentifier: kpi.timeseriesKey,
        measurementTypeKey: kpi.dataType,
        precision: kpi.timeseriesPrecision,
        unit: kpi.unit,
        highErrorThreshold: kpi.highErrorThreshold,
        highWarningThreshold: kpi.highWarningThreshold,
        lowErrorThreshold: kpi.lowErrorThreshold,
        lowWarningThreshold: kpi.lowWarningThreshold,
        highErrorThresholdList: kpi.highErrorThresholdList || 0,
        highWarningThresholdList: kpi.highWarningThresholdList || 0,
        lowErrorThresholdList: kpi.lowErrorThresholdList || 0,
        lowWarningThresholdList: kpi.lowWarningThresholdList || 0,
        thresholdList: kpi.thresholdList || [],
        timeseriesCategory: kpi?.timeseriesCategory,
        details: kpi?.details

    };
};

// eslint-disable-next-line import/no-unused-modules
export const formatChartDataV2 = (chartList, availableKPIs) => {
    const kpiList = getFlatAvailableKPIList(availableKPIs);
    // map through charts
    const formattedChartData = map(chartList, (chartData) => {
        return {
            chartID: chartData.chartID,
            chartName: chartData.chartName,
            loadingStatus: chartData.loadingStatus,
            zoom: chartData.zoom,
            // map through KPIs of the chart
            data: map(chartData.kpiList, (kpiData) => {
                return {
                    color: kpiData.color,
                    kpiIdentifier: kpiData.kpiIdentifier,
                    config: { ...getKPIConfigForChartV2(kpiList[kpiData.kpiIdentifier]), offset: kpiData.offset || 0 },
                    values: kpiData.values
                };
            })
        };
    });

    return formattedChartData;
};

// Merge all type of trends list to a sigle array
export const getMergedTrendKpiList = (trendKPIs) => {
    const mergedTrendKpiList = [];
    trendKPIs?.forEach((kpi) => {
        const kpiObj = kpi?.data;
        const keys = Object.keys(kpiObj);
        keys.forEach(key => {
            const newObj = kpiObj[key];
            mergedTrendKpiList.push(newObj);
        });
    });
    return mergedTrendKpiList;
};
